import { usePrevious } from '@calendly/hooks'
import { Segment } from '@calendly/segment'
import { useEffect, useRef } from 'react'
import { useLocation, useRouteMatch } from 'react-router-dom'

// Shared storage for timers across component instances (keyed by route pattern)
const routeTimers = new Map()

/**
 * Cancels any pending timer for the given route pattern
 * @param {string} routePattern - Route pattern to cancel timer for
 */
const cancelTimerForRoute = (routePattern) => {
  const existingTimerId = routeTimers.get(routePattern)
  if (existingTimerId) {
    clearTimeout(existingTimerId)
    routeTimers.delete(routePattern)
  }
}

/**
 * React hook that tracks an analytics event when a user exits a specific route/page.
 * Fires the tracking event after a delay once the user navigates away from the route.
 * Automatically cancels if the user returns to the route before the delay completes.
 * This is a Legacy Segment track event to enable Sprig to trigger a workflow.
 *
 * @param {string} routePattern - Route pattern to monitor (e.g., '/app/meetings')
 * @param {number} [delay=3000] - Delay in ms before firing the event after exit
 * @param {Object} [properties={}] - Additional properties to include in the event
 * @param {boolean} [ignoreSubRoutes=false] - If true, ignores sub-route navigation
 * (e.g., '/app/contacts' → '/app/contacts/user' won't fire event)
 * @param {string[]} [relatedRoutes=[]] - Array of route patterns that should be treated
 * as equivalent to the main route (e.g., navigating between them won't fire exit event)
 */
export const useTrackPageExit = ({
  routePattern,
  delay = 3000,
  properties = {},
  ignoreSubRoutes = false,
  relatedRoutes = []
}) => {
  const matchesRoutePattern = !!useRouteMatch(routePattern)
  const prevMatchesRoutePattern = usePrevious(matchesRoutePattern)
  const location = useLocation()
  // Snapshot of route data used when the user *exits* the route
  const exitSnapshotRef = useRef({
    path: null,
    url: null,
    properties,
  })

  // Capture location and properties while on the route (before navigation happens)
  useEffect(() => {
    if (matchesRoutePattern) {
      exitSnapshotRef.current = {
        path: location.pathname,
        url: window.location.href,
        properties,
      }
    }
  }, [matchesRoutePattern, location, properties])

  // Handle route entry/exit logic (runs when route matching state changes)
  useEffect(() => {
    // User ENTERED this route
    const userHasEnteredRoute = !prevMatchesRoutePattern && matchesRoutePattern && routePattern
    if (userHasEnteredRoute) {
      // Cancel any pending tracking event for this route pattern
      // (could have been set by a different component instance)
      cancelTimerForRoute(routePattern)

      // Cancel timers for related routes
      relatedRoutes.forEach((relatedRoute) => {
        cancelTimerForRoute(relatedRoute)
      })
    }

    // Cleanup function: runs on unmount or when dependencies change
    return () => {
      // Validate routePattern
      if (!routePattern) {
        return
      }

      const currentPath = window.location.pathname
      const matchedPath = exitSnapshotRef.current?.path

      // Check if current path is a sub-route when ignoreSubRoutes is enabled
      const isSubRoute = ignoreSubRoutes
        && (currentPath === routePattern || currentPath.startsWith(`${routePattern}/`))

      // Check if current path matches any related route
      const isRelatedRoute = relatedRoutes.some((route) => currentPath === route || currentPath.startsWith(`${route}/`))

      // Only schedule timer if we matched the route and have navigated away, not just re-running due to dependency change
      // If ignoreSubRoutes is true and we're on a sub-route, don't fire the event
      // If we're navigating to a related route, don't fire the event
      const userHasExitedRoute = matchesRoutePattern && currentPath !== matchedPath && !isSubRoute && !isRelatedRoute
      if (userHasExitedRoute) {
        // Cancel any existing timer for this route pattern
        cancelTimerForRoute(routePattern)

        const exitProperties = exitSnapshotRef.current?.properties

        // Schedule the tracking event
        const timerId = setTimeout(() => {
          Segment.track('Page Exit', {
            path: exitSnapshotRef.current?.path,
            url: exitSnapshotRef.current?.url,
            exit_duration_seconds: delay / 1000,
            ...exitProperties
          })
          routeTimers.delete(routePattern)
        }, delay)

        routeTimers.set(routePattern, timerId)
      }
    }
  }, [matchesRoutePattern, prevMatchesRoutePattern, routePattern, delay, ignoreSubRoutes, relatedRoutes])
}
